// The failed request's id: the reference an error shows for support (owner
// 2026-10-06). Every API response carries X-Request-ID, minted by core's
// request log (an inbound one is never trusted) and exposed to the browser by
// core's CORS; the same id is the request's row in the Request log, its
// audit changes and its error tracking occurrences.
//
// The API layers (shared/api.service.js, api-new/service/api.service.js)
// call tagRequestID on every failure, so whatever shape a layer hands on --
// axios's error, its response ({ status, data, headers }), the body alone
// (api-new's state.error) or a { error, status } wrapper -- carries the id as
// a non-enumerable `requestID` (with `requestAt`, when it failed), and
// requestIDOf reads it back from any of them. Non-enumerable: a body that is
// shown, spread or stringified elsewhere is unchanged. A network failure has
// no response and so no id; a successful response is never touched.

export const REQUEST_ID_HEADER = 'x-request-id'

// A request id as core mints it (a UUID); anything else is not shown.
const SHAPE = /^[0-9a-fA-F-]{8,64}$/

const valid = v => (typeof v === 'string' && SHAPE.test(v) ? v : null)

// Whether v looks like a request id (an id from a URL, say).
export const isRequestID = v => valid(v) !== null

const headerOf = response => {
  const headers = response && typeof response === 'object' ? response.headers : null
  if (!headers) return null
  if (typeof headers.get === 'function') {
    const v = headers.get(REQUEST_ID_HEADER)
    if (v) return valid(String(v))
  }
  return valid(headers[REQUEST_ID_HEADER] || headers['X-Request-ID'] || headers['X-Request-Id'])
}

const own = (o, key) => (o && typeof o === 'object' && Object.prototype.hasOwnProperty.call(o, key) ? o[key] : undefined)

const define = (o, key, value) => {
  if (!o || typeof o !== 'object' || Object.isFrozen(o) || Object.prototype.hasOwnProperty.call(o, key)) return
  try {
    Object.defineProperty(o, key, { value, enumerable: false, configurable: true, writable: true })
  } catch (e) {
    // a body that can't take a property keeps no reference; the error does
  }
}

// Marks error (axios's), its response and the response's body with the
// failed request's id and when it failed. Returns error, for `throw`.
// expected: an answer the caller asked for and shows nobody (a thumbnail
// with nothing uploaded, getBlobAsync's quietNotFound): tagged the same, but
// not remembered as the page's last failed request, so a browser error
// report never names it.
export const tagRequestID = (error, { expected = false } = {}) => {
  const response = error && typeof error === 'object' ? error.response : null
  const id = headerOf(response)
  if (!id) return error
  const at = new Date().toISOString()
  if (!expected) lastFailed = { id, time: Date.now() }
  for (const o of [error, response, response.data]) {
    define(o, 'requestID', id)
    define(o, 'requestAt', at)
  }
  return error
}

// The last failed request this page saw (tagRequestID), for a browser error
// report: a crash right after a failed call is often caused by it.
let lastFailed = null

// The id of the last request that failed within the last withinMs, or null.
export const lastFailedRequestID = (withinMs = 60000) =>
  lastFailed && Date.now() - lastFailed.time <= withinMs ? lastFailed.id : null

// Forgets the last failed request (tests).
export const resetLastFailedRequest = () => { lastFailed = null }

// Copies the reference from source (any shape above) onto target, an object
// a layer builds from the failure (api.service's { error, status }).
export const carryRequestID = (target, source) => {
  const id = requestIDOf(source)
  if (id) {
    define(target, 'requestID', id)
    define(target, 'requestAt', requestAtOf(source))
  }
  return target
}

const shapesOf = error => {
  if (!error || typeof error !== 'object') return []
  const inner = typeof error.error === 'object' ? error.error : null
  return [error, error.response, error.data, inner, error.response && error.response.data]
}

// The failed request's id, from any shape an API layer hands on; null when
// there is none (a network failure, a client-side error, a string).
export const requestIDOf = error => {
  for (const o of shapesOf(error)) {
    const id = valid(own(o, 'requestID')) || headerOf(o)
    if (id) return id
  }
  return null
}

// When the request failed (ISO 8601), when the layer recorded it.
export const requestAtOf = error => {
  for (const o of shapesOf(error)) {
    const at = own(o, 'requestAt')
    if (typeof at === 'string' && at) return at
  }
  return null
}
